import { useSyncExternalStore } from 'react';
import { hasFeatureEntitlement } from '@alltrails/context/hooks/useFeatureEntitlements';
import useUser from '@alltrails/context/hooks/useUser';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import { advancedBuilderPath, type CustomRouteBuilderPath, standardBuilderPath } from '../utils/customRouteBuilderPaths';
import { isAdvancedBuilderRedirectSnoozed, isStandardBuilderPreferred } from '../utils/customRouteBuilderPreference';

// Only read for the first client render: the stored values change on the way into or out of a builder, which leaves the page anyway
const subscribe = () => () => {};

// The server can't read localStorage, so it renders as if nothing were stored and the client corrects it after hydration
const getServerSnapshot = () => false;

/**
 * The base path of the custom route builder a "Build a route" CTA should open; append `/new`, `/trail/<slug>` or `/<slug>`.
 * Logged-out users always get the advanced builder, where the auth wall checks their entitlements after sign-in. Users with the
 * build_your_own_trail entitlement get it unless they switched back to the standard builder, and everyone else gets it at most once a week.
 */
const useCustomRouteBuilderPath = (): CustomRouteBuilderPath => {
  const user = useUser();
  const isAdvancedBuilderEnabled = useExperiments()['web-adv-rm-advanced-custom-route-builder']?.value === 'on';
  const prefersStandard = useSyncExternalStore(subscribe, isStandardBuilderPreferred, getServerSnapshot);
  const isRedirectSnoozed = useSyncExternalStore(subscribe, isAdvancedBuilderRedirectSnoozed, getServerSnapshot);

  if (!isAdvancedBuilderEnabled) {
    return standardBuilderPath;
  }

  if (!user) {
    return advancedBuilderPath;
  }

  if (hasFeatureEntitlement(user, 'build_your_own_trail')) {
    return prefersStandard ? standardBuilderPath : advancedBuilderPath;
  }

  return isRedirectSnoozed ? standardBuilderPath : advancedBuilderPath;
};

export default useCustomRouteBuilderPath;
